import React, { useEffect, useRef, useState } from 'react';
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faEdit } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";

const EditableCell = ({ rowData, field, editing, onChange, originalValue }) => {
    const [value, setValue] = useState(originalValue);

    const handleOnChange = e => {
        setValue(e.target.value);
        onChange?.(field, e.target.value);
    };

    if (editing) {
        return (
            <input
                type="text"
                className="form-control"
                name={field}
                onChange={handleOnChange}
                value={value ?? ''}
            />
        );
    }

    return originalValue ?? '';
};

export const EditableTable = ({ data, columns, getEditedData, customAction, columnDeps = [] }) => {
    const { t } = useTranslation('translations');
    const [innerData, setInnerData] = useState(data);
    const [editingId, setEditingId] = useState('');
    const formDataRef = useRef({});

    const isEditing = (record) => record.id === editingId;

    useEffect(() => {
        setInnerData(data);
        setEditingId('');
        formDataRef.current = {};
    }, [data]);

    const handleFieldChange = (field, value) => {
        formDataRef.current = {
            ...formDataRef.current,
            [field]: value,
        };
    };

    const edit = (record) => {
        formDataRef.current = {};
        setEditingId(record.id);
    };

    const cancel = () => {
        formDataRef.current = {};
        setEditingId('');
    };

    const save = (item) => {
        const payload = { ...item, ...formDataRef.current };
        const tempData = [...innerData];
        const index = tempData.findIndex(row => editingId === row.id);

        if (index > -1) {
            const originalItem = tempData[index];
            tempData.splice(index, 1, {
                ...originalItem,
                ...payload,
            });
            setEditingId('');
            getEditedData(originalItem, payload);
            setInnerData(tempData);
            formDataRef.current = {};
        }
    };

    const getCellValue = (rowData, col) => {
        // Handle nested selectors like "provider.name"
        if (typeof col.selector === 'function') {
            return col.selector(rowData);
        }
        if (typeof col.selector === 'string') {
            return col.selector.split('.').reduce((obj, key) => obj?.[key], rowData);
        }
        return rowData[col.selector];
    };

    const bodyTemplate = (rowData, col) => {
        const editing = isEditing(rowData);
        const field = typeof col.selector === 'string' ? col.selector : col.name;
        const value = getCellValue(rowData, col);

        if (col.editable && editing) {
            return (
                <EditableCell
                    rowData={rowData}
                    field={field}
                    editing={true}
                    onChange={handleFieldChange}
                    originalValue={value}
                />
            );
        }

        return value ?? '';
    };

    const actionsBodyTemplate = (rowData) => {
        if (customAction) {
            return customAction({
                row: rowData,
                isEditing: isEditing(rowData),
                edit,
                save,
                cancel
            });
        }

        const editing = isEditing(rowData);

        if (editing) {
            return (
                <div className="d-flex gap-2">
                    <button
                        type="button"
                        style={{ padding: '0.2em', marginRight: '0.5em' }}
                        className="btn btn-primary btn-sm"
                        onClick={() => save(rowData)}
                    >
                        <FontAwesomeIcon icon={faCheck} className="mr-2" />
                        {t('common.save')}
                    </button>
                    <button
                        type="button"
                        style={{ padding: '0.2em' }}
                        className="btn btn-secondary btn-sm"
                        onClick={cancel}
                    >
                        {t('common.cancel')}
                    </button>
                </div>
            );
        }

        return (
            <button
                type="button"
                onClick={() => edit(rowData)}
                className="btn btn-outline-info btn-sm rounded-pill"
            >
                <FontAwesomeIcon icon={faEdit} className="mr-1" />
                {t('common.edit')}
            </button>
        );
    };

    return (
        <DataTable
            value={innerData}
            dataKey="id"
            size="small"
            stripedRows
        >
            {columns.map((col, index) => (
                <Column
                    key={col.selector || col.name || index}
                    field={typeof col.selector === 'string' ? col.selector : undefined}
                    header={col.name}
                    body={(rowData) => bodyTemplate(rowData, col)}
                    sortable={col.sortable}
                    style={col.style}
                />
            ))}
            <Column
                header={t('cashRegister.Actions')}
                body={actionsBodyTemplate}
                style={{ width: '150px' }}
            />
        </DataTable>
    );
};
